import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, defer, map, of, retry, throwError, timer } from 'rxjs';
import { AlertService } from '@core/services/alert.service';
import { SubscriptionFeaturesService } from '@core/services/subscription-features.service';

export const featureGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const alertService = inject(AlertService);
  const subscriptionFeaturesService = inject(SubscriptionFeaturesService);

  const featureKey = String(route.data?.['feature'] ?? '');
  const redirectTo = String(route.data?.['redirectTo'] ?? '/pricing');
  const blockedMessage =
    String(route.data?.['blockedMessage'] ?? 'Actualiza tu plan para acceder.');
  const comingSoonMessage =
    String(route.data?.['comingSoonMessage'] ?? 'Esta funcionalidad estará disponible próximamente.');

  if (!featureKey) {
    return of(true);
  }

  return defer(() => subscriptionFeaturesService.load()).pipe(
    retry({
      count: 2,
      delay: (error, attempt) => {
        const status = error?.status;
        return status === 0 || status === 502 || status === 503 || status === 504
          ? timer(attempt * 500)
          : throwError(() => error);
      },
    }),
    map(() => {
      if (subscriptionFeaturesService.isComingSoon(featureKey)) {
        alertService.open({ type: 'info', message: comingSoonMessage });
        return router.createUrlTree([redirectTo], {
          queryParams: { from: state.url, feature: featureKey, reason: 'coming-soon' },
        });
      }

      if (subscriptionFeaturesService.isEnabled(featureKey)) {
        return true;
      }

      alertService.open({ type: 'warning', message: blockedMessage });
      return router.createUrlTree([redirectTo], {
        queryParams: { from: state.url, feature: featureKey, reason: 'upgrade' },
      });
    }),
    catchError(() => {
      alertService.open({
        type: 'warning',
        message: 'No pudimos verificar tus permisos. Intenta entrar de nuevo en unos momentos.',
      });
      return of(false);
    }),
  );
};
